import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, InputRightElement, useMultiStyleConfig} from '@chakra-ui/react'
import {CheckIcon, InfoIcon} from '../icons'
import InfoPopover from '../popovers/info-popover'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import flags from 'country-flag-icons/react/3x2'
import {useLocaleCountry} from '../../hooks/use-locale-custom'

const PhoneComponent = React.forwardRef((props, ref) => {
    const {
        isSuccess,
        isInvalid,
        tooltip,
        tooltipVariant,
        tooltipOpensOn,
        name,
        value,
        rules,
        // variant,
        hideSuccessIcon,
        placeholder = '',
        isDisabled,
        onChange,
    } = props

    const status = isDisabled
        ? 'disabled'
        : isInvalid
        ? 'invalid'
        : isSuccess
        ? 'success'
        : undefined

    const defaultCountry = useLocaleCountry().toUpperCase()
    const placeholderText = props.label || placeholder

    // Show placeholder when there are no digits entered yet
    const isOnlyCountryCode = !value
    const hasUserInput = !isOnlyCountryCode

    const styles = useMultiStyleConfig('Field', {
        variant: status,
        isInitial: hasUserInput,
    })

    return (
        <Box __css={styles.container} data-initial={hasUserInput || undefined}>
            {isOnlyCountryCode && placeholderText && (
                <Text
                    {...styles.placeholderLabel}
                    position="absolute"
                    top="50%"
                    transform="translateY(-50%)"
                    left="80px"
                    zIndex={1}
                    pointerEvents="none"
                    textStyle="body-small"
                    color="foreground.neutral.subtle"
                    data-testid="phone-placeholder-label"
                >
                    {placeholderText}
                </Text>
            )}
            {hasUserInput && placeholderText && (
                <Text
                    {...styles.placeholderLabel}
                    position="absolute"
                    top="6px"
                    left="12px"
                    zIndex={1}
                    pointerEvents="none"
                    data-testid="phone-placeholder-label-active"
                >
                    {placeholderText}
                </Text>
            )}
            <PhoneInput
                defaultCountry={defaultCountry}
                value={value}
                onChange={(val) => onChange(val || null)}
                placeholder=""
                international={true}
                countryCallingCodeEditable={false}
                id={name}
                name={name}
                ref={ref}
                required={rules?.required}
                flags={flags}
            />
            {status === 'success' && (
                <InputRightElement {...styles.inputRightElement}>
                    <CheckIcon
                        {...{...styles.successIcon, display: hideSuccessIcon ? 'none' : 'block'}}
                    />
                </InputRightElement>
            )}
            {tooltip && (
                <InputRightElement {...styles.inputRightElement}>
                    <InfoPopover
                        trigger={
                            <Box>
                                <InfoIcon {...styles.infoIcon} />
                            </Box>
                        }
                        variant={tooltipVariant}
                        openTooltipOn={tooltipOpensOn}
                    />
                </InputRightElement>
            )}
        </Box>
    )
})

PhoneComponent.displayName = 'PhoneComponent'
PhoneComponent.propTypes = {
    label: PropTypes.string,
    isDisabled: PropTypes.bool,
    isInvalid: PropTypes.bool,
    tooltip: PropTypes.string,
    tooltipVariant: PropTypes.string,
    tooltipOpensOn: PropTypes.string,
    isSuccess: PropTypes.bool,
    isRequired: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string,
    variant: PropTypes.string,
    shouldRemoveLabel: PropTypes.bool,
    value: PropTypes.string,
    rules: PropTypes.object,
    isProductRegistration: PropTypes.bool,
    'aria-label': PropTypes.string,
    borderColor: PropTypes.string,
    hideSuccessIcon: PropTypes.bool,
    type: PropTypes.string,
}

export default PhoneComponent
